import React from 'react';
import Select, { components, StylesConfig } from 'react-select';
import { useTheme } from 'styled-components';
import styled from 'styled-components';
import { Theme } from 'styles/theme';
import { ReactComponent as ChevronDownIconComponent } from 'images/v2/icons/svg/fill/svg/chevron-down.svg';
import { ReactComponent as ChevronUpIconComponent } from 'images/v2/icons/svg/fill/svg/chevron-up.svg';
import { ReactComponent as CrossIconComponent } from 'images/v2/icons/svg/fill/svg/cross.svg';
import { ReactComponent as CheckIconComponent } from 'images/v2/icons/svg/fill/svg/check.svg';
import Tooltip from 'components/controls/Tooltip';
import { Option } from 'types/types';

interface SingleProps {
  isMulti?: false;
  value: Option | null;
  onChange: (selected: Option | null) => void;
}

interface MultiProps {
  isMulti: true;
  value: Option[];
  onChange: (selected: Option[]) => void;
}

type Props = (MultiProps | SingleProps) & {
  options: Option[];
  label: string;
};

const FILTER_LABEL_PROP = 'filterLabel';
const ALL_OPTION: Option = { label: 'All', value: '' };

const CustomValueContainer = ({ children, getValue, selectProps, ...props }: any) => {
  const selected: Option[] = getValue();
  const filterLabel: string = selectProps[FILTER_LABEL_PROP];

  let primaryLabel = 'All';
  let extra = 0;
  if (selected.length > 0) {
    extra = selected.length - 1;
    primaryLabel = selected[0].label;
  }

  const tooltipText = selected.map((o: Option) => o.label).join(', ');

  const childrenArray = React.Children.toArray(children);
  const input = childrenArray[childrenArray.length - 1];

  return (
    <components.ValueContainer getValue={getValue} selectProps={selectProps} {...props}>
      <ValueSummary>
        <FilterLabel>{filterLabel}:</FilterLabel>{' '}
        {selected.length > 0 ? (
          <LabelWrapper>
            <PrimaryLabel>{primaryLabel}</PrimaryLabel>
            {extra > 0 && <ExtraBadge> (+{extra})</ExtraBadge>}
          </LabelWrapper>
        ) : (
          <PrimaryLabel>All</PrimaryLabel>
        )}
        {selected.length > 0 &&
          (extra > 0 ? (
            <Tooltip position="mouse">{tooltipText}</Tooltip>
          ) : (
            <Tooltip position="titleEllipsed">{tooltipText}</Tooltip>
          ))}
      </ValueSummary>
      <HiddenInput>{input}</HiddenInput>
    </components.ValueContainer>
  );
};

const FilterSelect: React.FC<Props> = ({ options, value, onChange, label, isMulti }) => {
  const theme = useTheme() as Theme;
  const isMultiMode = isMulti === true;

  const styles: StylesConfig<Option, boolean> = {
    control: (base, state) => {
      const borderColor = state.isFocused ? theme.colors.primaryOrange : 'transparent';
      return {
        ...base,
        background: state.menuIsOpen ? theme.colors.primaryWhite : theme.colors.elementsDropDown,
        border: `1.5px solid ${borderColor}`,
        '&:hover': { borderColor },
        borderRadius: 4,
        minHeight: 40,
        height: 40,
        boxShadow: 'none',
        cursor: 'pointer',
        flexWrap: 'nowrap',
        fontFamily: "'Inter'",
      };
    },
    valueContainer: (base) => ({
      ...base,
      padding: '0 0 0 14px',
      flexWrap: 'nowrap',
      overflow: 'hidden',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      alignItems: 'center',
      gap: 2,
      paddingRight: 10,
      '> div': {
        padding: 0,
        display: 'flex',
        alignItems: 'center',
      },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    clearIndicator: (base) => ({
      ...base,
      padding: 0,
      color: theme.colors.primaryBlack,
      cursor: 'pointer',
      '&:hover': { color: theme.colors.primaryOrange },
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: 0,
      color: theme.colors.primaryBlack,
      cursor: 'pointer',
      '&:hover': { color: theme.colors.primaryOrange },
    }),
    menu: (base) => ({
      ...base,
      marginTop: 2,
      boxShadow: 'inset 0 0 0 1px hsla(0,0%,0%,0.1), 0 4px 11px hsla(0,0%,0%,0.1)',
      '> div': { paddingTop: 0, paddingBottom: 0 },
    }),
    option: (base, state) => ({
      ...base,
      fontSize: 14,
      fontFamily: "'Inter'",
      color: theme.colors.primaryBlack,
      fontWeight: state.isSelected ? 600 : 400,
      backgroundColor: state.isFocused ? theme.colors.elementsFilledInput : theme.colors.primaryWhite,
      cursor: state.isSelected ? 'default' : 'pointer',
      '&:active': { backgroundColor: theme.colors.elementsFilledInput },
    }),
    input: (base) => ({
      ...base,
      caretColor: 'transparent',
      color: 'inherit',
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 999999,
    }),
  };

  const formatOptionLabel = (option: Option, { context, selectValue }: any) => {
    if (context === 'menu') {
      const isAllOption = option.value === ALL_OPTION.value;
      const currentValue = selectValue as Option[];
      const isSelected = isAllOption
        ? currentValue.length === 0 || currentValue[0]?.value === ALL_OPTION.value
        : currentValue.some((s) => s.value === option.value);
      return (
        <OptionRow>
          <OptionLabel $bold={isSelected}>{option.label}</OptionLabel>
          {isSelected && <CheckIconComponent />}
        </OptionRow>
      );
    }
    return option.label;
  };

  const commonProps = {
    components: { ClearIndicator, DropdownIndicator, ValueContainer: CustomValueContainer },
    styles,
    ...{ [FILTER_LABEL_PROP]: label },
    formatOptionLabel,
    isSearchable: true,
    placeholder: '',
    controlShouldRenderValue: false,
    menuPortalTarget: document.body,
    menuPosition: 'fixed' as const,
    isClearable: true,
  };

  if (!isMultiMode) {
    const singleOptions = [ALL_OPTION, ...options];
    return (
      <Select<Option, false>
        {...commonProps}
        options={singleOptions}
        isMulti={false}
        value={value as Option | null}
        onChange={(v) => (onChange as (s: Option | null) => void)(v?.value === ALL_OPTION.value ? null : v)}
      />
    );
  }

  return (
    <Select<Option, true>
      {...commonProps}
      options={[ALL_OPTION, ...options]}
      isMulti
      value={value as Option[]}
      onChange={(v) => {
        const selected = v ? (v as Option[]) : [];
        const allChosen = selected.some((s) => s.value === ALL_OPTION.value);

        (onChange as (s: Option[]) => void)(allChosen ? [] : selected);
      }}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
    />
  );
};

const LabelWrapper = styled.span`
  position: relative;
`;

const HiddenInput = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
`;

const ExtraBadge = styled.span`
  flex-shrink: 0;
  font-weight: 600;
`;

const PrimaryLabel = styled.span`
  font-weight: 600;
`;

const ValueSummary = styled.span`
  font-size: 14px;
  color: ${(props) => props.theme.colors.primaryBlack};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const OptionLabel = styled.span<{ $bold?: boolean }>`
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
`;

const FilterLabel = styled.span`
  color: ${(props) => props.theme.colors.primaryBlack70};
`;

const FilterCrossIcon = styled(CrossIconComponent)`
  width: 14px;
  height: 14px;
  color: ${(props) => props.theme.colors.primaryBlack};
  &:hover {
    color: ${(props) => props.theme.colors.primaryOrange};
  }
`;

const FilterChevronIcon = styled(ChevronDownIconComponent)`
  width: 14px;
  height: 14px;
  color: ${(props) => props.theme.colors.primaryBlack};
  &:hover {
    color: ${(props) => props.theme.colors.primaryOrange};
  }
`;

const FilterChevronUpIcon = styled(ChevronUpIconComponent)`
  width: 14px;
  height: 14px;
  color: ${(props) => props.theme.colors.primaryBlack};
  &:hover {
    color: ${(props) => props.theme.colors.primaryOrange};
  }
`;

const ClearIndicator = (props: any) => (
  <components.ClearIndicator {...props}>
    <FilterCrossIcon />
  </components.ClearIndicator>
);

const DropdownIndicator = (props: any) => {
  const isOpen = props.selectProps.menuIsOpen;
  return (
    <components.DropdownIndicator {...props}>
      {isOpen ? <FilterChevronUpIcon /> : <FilterChevronIcon />}
    </components.DropdownIndicator>
  );
};

export default FilterSelect;
